/*
 * Keller native low-stock counter.
 *
 * Reads the selected variant from the theme's own authoritative hidden variant input and
 * applies the same inventory rules the Liquid snippet applied on the server, so the initial
 * render and every later variant change agree.
 *
 * Inventory data comes from a minimal map rendered by snippets/keller-low-stock-counter.liquid.
 * Products above Liquid's 250-variant limit get no map and are resolved through the Section
 * Rendering API instead, so a decision is never made from an incomplete map.
 */
(function () {
  const TAG_NAME = 'keller-low-stock';

  if (!window.customElements || window.customElements.get(TAG_NAME)) {
    return;
  }

  function messageFor(quantity) {
    return quantity === 1 ? 'Only 1 left' : 'Only ' + quantity + ' left';
  }

  /*
   * Returns the quantity to display, or null when the counter must stay hidden.
   * The order below mirrors the documented rule precedence.
   */
  function qualifyingQuantity(state, threshold, preorderMarker) {
    if (!state || state.tracked !== true) {
      return null;
    }

    const quantity = typeof state.quantity === 'number' ? state.quantity : null;
    if (quantity === null) {
      return null;
    }

    const activePreorder =
      quantity <= 0 && state.policy === 'continue' && (preorderMarker || state.preorderPlan === true);
    if (activePreorder) {
      return null;
    }

    if (state.available !== true || quantity < 1 || quantity > threshold) {
      return null;
    }

    return quantity;
  }

  class KellerLowStock extends HTMLElement {
    constructor() {
      super();
      this.onSectionChange = this.onSectionChange.bind(this);
      this.frame = null;
      this.request = null;
      this.requestToken = 0;
      this.cachedInput = null;
    }

    connectedCallback() {
      this.messageEl = this.querySelector('[data-keller-low-stock-message]');
      this.textEl = this.querySelector('[data-keller-low-stock-text]');
      // The "LOW IN STOCK!" label is only in the DOM while the counter is showing.
      this.labelEl = this.querySelector('[data-keller-low-stock-label]');

      if (!this.messageEl || !this.textEl) {
        return;
      }

      this.sectionId = this.dataset.sectionId || '';
      this.productUrl = this.dataset.productUrl || '';
      this.suppressed = this.dataset.suppressed === 'true';
      this.preorderMarker = this.dataset.preorderMarker === 'true';
      this.highVariantCount = this.dataset.highVariantCount === 'true';
      this.threshold = parseInt(this.dataset.threshold, 10) || 10;
      this.currentVariantId = this.dataset.initialVariantId || null;
      this.variants = this.readVariantMap();

      // A suppressed product never shows a counter, so there is nothing to listen for.
      if (this.suppressed) {
        return;
      }

      this.section =
        this.closest('[data-section-type="product__section"]') || this.closest('[data-section-id]');

      if (!this.section) {
        return;
      }

      this.section.addEventListener('change', this.onSectionChange);

      // The server rendered the initial variant, so this only does work if something
      // (a deep link handler, or a swatch app preselecting an option) already moved on.
      this.sync();
    }

    disconnectedCallback() {
      if (this.section) {
        this.section.removeEventListener('change', this.onSectionChange);
      }
      if (this.frame !== null) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
      if (this.request) {
        this.request.abort();
        this.request = null;
      }
    }

    readVariantMap() {
      const script = this.querySelector('script[type="application/json"][data-keller-low-stock-data]');
      if (!script) {
        return null;
      }
      try {
        const parsed = JSON.parse(script.textContent);
        return parsed && parsed.variants ? parsed.variants : null;
      } catch (error) {
        return null;
      }
    }

    /*
     * The section contains several forms that carry a hidden `id` input, including the
     * payment-terms form and any upsell product, so match the main product form by id.
     */
    variantInput() {
      if (this.cachedInput && this.cachedInput.isConnected) {
        return this.cachedInput;
      }

      const wantedId = 'product-form-' + this.sectionId;
      const forms = this.section.getElementsByTagName('form');

      for (let index = 0; index < forms.length; index += 1) {
        // getAttribute, not .id: a form containing <input name="id"> exposes that control
        // as form.id through the named-getter, which shadows the id attribute.
        if (forms[index].getAttribute('id') !== wantedId) {
          continue;
        }
        this.cachedInput =
          forms[index].querySelector('input.formVariantId[name="id"]') ||
          forms[index].querySelector('input[name="id"]');
        return this.cachedInput;
      }

      this.cachedInput = null;
      return null;
    }

    /*
     * Any change inside the section is a candidate: the theme dispatches a bubbling change on
     * the hidden variant input once it has resolved a variant, and the swatch app drives the
     * theme's own selectors. Reading on the next frame keeps this independent of handler order.
     */
    onSectionChange() {
      if (this.frame !== null) {
        return;
      }
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    }

    sync() {
      const input = this.variantInput();
      if (!input) {
        return;
      }

      const variantId = String(input.value || '').trim();
      if (!variantId || variantId === this.currentVariantId) {
        return;
      }

      this.currentVariantId = variantId;

      if (this.highVariantCount) {
        this.syncFromSectionRender(variantId);
        return;
      }

      if (!this.variants) {
        this.hide();
        return;
      }

      const quantity = qualifyingQuantity(
        this.variants[variantId],
        this.threshold,
        this.preorderMarker
      );

      if (quantity === null) {
        this.hide();
      } else {
        this.show(messageFor(quantity));
      }
    }

    /*
     * Above 250 variants Liquid cannot render a complete map, so ask the server to re-render
     * this section for the selected variant and reuse its decision verbatim.
     */
    syncFromSectionRender(variantId) {
      if (!this.productUrl || !this.sectionId) {
        this.hide();
        return;
      }

      if (this.request) {
        this.request.abort();
      }

      this.request = new AbortController();
      this.requestToken += 1;

      const token = this.requestToken;
      const separator = this.productUrl.indexOf('?') === -1 ? '?' : '&';
      const url =
        this.productUrl +
        separator +
        'section_id=' +
        encodeURIComponent(this.sectionId) +
        '&variant=' +
        encodeURIComponent(variantId);

      fetch(url, { signal: this.request.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Section render request failed');
          }
          return response.text();
        })
        .then((html) => {
          if (token !== this.requestToken || !this.isConnected) {
            return;
          }
          this.applySectionRender(html);
        })
        .catch(() => {
          // A failed or superseded response must never leave a possibly wrong number behind.
          if (token !== this.requestToken || !this.isConnected) {
            return;
          }
          this.hide();
        });
    }

    applySectionRender(html) {
      const rendered = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-keller-low-stock-message]');

      if (!rendered || rendered.hasAttribute('hidden')) {
        this.hide();
        return;
      }

      const renderedText = rendered.querySelector('[data-keller-low-stock-text]');
      const text = renderedText ? renderedText.textContent.trim() : '';

      if (!text) {
        this.hide();
        return;
      }

      this.show(text);
    }

    show(text) {
      if (this.labelEl) {
        this.labelEl.textContent = this.labelEl.dataset.label || '';
      }
      this.textEl.textContent = text;
      this.messageEl.removeAttribute('hidden');
    }

    hide() {
      this.messageEl.setAttribute('hidden', '');
      this.textEl.textContent = '';
      if (this.labelEl) {
        this.labelEl.textContent = '';
      }
    }
  }

  window.customElements.define(TAG_NAME, KellerLowStock);
})();
